import { createBrowserRouter, createRoutesFromElements, Navigate, Route, useParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'

import { AppLayout } from '@/app/layout/AppLayout'
import { ProtectedRoute } from '@/components/common/ProtectedRoute'
import { ProjectLayout } from '@/components/common/ProjectLayout'
import { CandidateDeepDiveConfirmation } from '@/features/candidates/pages/CandidateDeepDiveConfirmation'
import { CandidateDeepDiveConversation } from '@/features/candidates/pages/CandidateDeepDiveConversation'
import { CandidateDeepDiveStart } from '@/features/candidates/pages/CandidateDeepDiveStart'
import { CandidateDeepDiveSummary } from '@/features/candidates/pages/CandidateDeepDiveSummary'
import { CandidateReview } from '@/features/projects/pages/CandidateReview'
import { CandidatePipelineBoard } from '@/features/projects/pages/CandidatePipelineBoard'
import { Confirmation } from '@/features/projects/pages/Confirmation'
import { ContextReview } from '@/features/projects/pages/ContextReview'
import { ConversationalIntake } from '@/features/projects/pages/ConversationalIntake'
import { DeepProbe } from '@/features/projects/pages/DeepProbe'
import { Home } from '@/features/projects/pages/Home'
import { NewProject } from '@/features/projects/pages/NewProject'
import { OutputEditor } from '@/features/projects/pages/OutputEditor'
import { OutputGeneration } from '@/features/projects/pages/OutputGeneration'
import { OutreachPreview } from '@/features/projects/pages/OutreachPreview'
import { OutreachTracker } from '@/features/projects/pages/OutreachTracker'
import { PreScreenResults } from '@/features/projects/pages/PreScreenResults'
import { PreScreenTrigger } from '@/features/projects/pages/PreScreenTrigger'
import { CandidatePreScreen } from '@/features/prescreen/CandidatePreScreen'
import { ProjectDashboard } from '@/features/projects/pages/ProjectDashboard'
// ProjectPipeline removed — was a static mock; /project/:id now redirects to /dashboard
import { UploadDocuments } from '@/features/projects/pages/UploadDocuments'
import { ForgotPassword } from '@/features/system/pages/ForgotPassword'
import { Login } from '@/features/system/pages/Login'
import { NotFound } from '@/features/system/pages/NotFound'
import { ResetPassword } from '@/features/system/pages/ResetPassword'
import { Settings } from '@/features/system/pages/Settings'
import { Signup } from '@/features/system/pages/Signup'
import { ApplyPage } from '@/features/apply/pages/ApplyPage'
import { LandingPage } from '@/features/marketing/pages/LandingPage'
import { BlogIndex } from '@/features/marketing/pages/BlogIndex'
import { BlogPost } from '@/features/marketing/pages/BlogPost'
import { TaskBoard } from '@/features/tasks/pages/TaskBoard'
import { CostDashboard } from '@/features/costs/pages/CostDashboard'
import { AnalyticsDashboard } from '@/features/analytics/pages/AnalyticsDashboard'

// Root: show landing page for public visitors, redirect to /home for authenticated users
function RootRedirect() {
  const { user, isLoading } = useAuth()
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#02182B] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#FD802E] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }
  if (user) return <Navigate to="/home" replace />
  return <LandingPage />
}

// Redirect /project/:id → /project/:id/dashboard
function ProjectDashboardRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/project/${id}/dashboard`} replace />
}

export const router = createBrowserRouter(
  createRoutesFromElements(
    <>
    {/* Root — public landing page; redirects authenticated users to /home */}
    <Route index element={<RootRedirect />} />

    {/* Standalone — no app chrome */}
    <Route path="/landing" element={<LandingPage />} />

    <Route element={<AppLayout />}>
      {/* Public — no auth */}
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/apply/:projectId" element={<ApplyPage />} />

      {/* Auth — public */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Main — protected */}
      <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><TaskBoard /></ProtectedRoute>} />
      <Route path="/costs" element={<ProtectedRoute><CostDashboard /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/new-project" element={<ProtectedRoute><NewProject /></ProtectedRoute>} />

      {/* Project flows — 5-stage model: Upload → Intake → Confirmation → Outputs → Pipeline */}
      <Route path="/project/:id/upload" element={<ProtectedRoute><ProjectLayout stage="upload"><UploadDocuments /></ProjectLayout></ProtectedRoute>} />
      {/* context-review kept as redirect to intake for legacy URLs */}
      <Route path="/project/:id/context-review" element={<ProtectedRoute><ProjectLayout stage="intake"><ConversationalIntake /></ProjectLayout></ProtectedRoute>} />
      <Route path="/project/:id/intake" element={<ProtectedRoute><ProjectLayout stage="intake"><ConversationalIntake /></ProjectLayout></ProtectedRoute>} />
      {/* deep-probe redirects to intake — deep probing now happens inside intake session */}
      <Route path="/project/:id/deep-probe" element={<ProtectedRoute><ProjectLayout stage="intake"><ConversationalIntake /></ProjectLayout></ProtectedRoute>} />
      <Route path="/project/:id/confirmation" element={<ProtectedRoute><ProjectLayout stage="confirmation"><Confirmation /></ProjectLayout></ProtectedRoute>} />
      <Route path="/project/:id/output" element={<ProtectedRoute><ProjectLayout stage="outputs"><OutputGeneration /></ProjectLayout></ProtectedRoute>} />
      <Route path="/project/:id/editor" element={<ProtectedRoute><ProjectLayout stage="outputs"><OutputEditor /></ProjectLayout></ProtectedRoute>} />
      <Route path="/project/:id/dashboard" element={<ProtectedRoute><ProjectLayout stage="dashboard"><ProjectDashboard /></ProjectLayout></ProtectedRoute>} />

      {/* Pipeline views — protected */}
      {/* /project/:id redirects to /dashboard — ProjectPipeline was a static mock, removed */}
      <Route path="/project/:id" element={<ProjectDashboardRedirect />} />
      <Route path="/project/:id/pipeline" element={<ProtectedRoute><ProjectLayout stage="dashboard"><CandidatePipelineBoard /></ProjectLayout></ProtectedRoute>} />

      {/* Outreach — protected */}
      <Route path="/project/:id/outreach" element={<ProtectedRoute><OutreachTracker /></ProtectedRoute>} />

      {/* Candidate — protected */}
      <Route path="/project/:id/candidate/:candidateId" element={<ProtectedRoute><CandidateReview /></ProtectedRoute>} />
      <Route path="/project/:id/candidate/:candidateId/outreach" element={<ProtectedRoute><OutreachPreview /></ProtectedRoute>} />
      <Route path="/project/:id/candidate/:candidateId/pre-screen" element={<ProtectedRoute><PreScreenTrigger /></ProtectedRoute>} />
      <Route path="/project/:id/candidate/:candidateId/pre-screen/results" element={<ProtectedRoute><PreScreenResults /></ProtectedRoute>} />

      {/* Candidate Deep Dive — protected */}
      <Route path="/candidate/deep-dive/start" element={<ProtectedRoute><CandidateDeepDiveStart /></ProtectedRoute>} />
      <Route path="/candidate/deep-dive/conversation" element={<ProtectedRoute><CandidateDeepDiveConversation /></ProtectedRoute>} />
      <Route path="/candidate/deep-dive/confirmation" element={<ProtectedRoute><CandidateDeepDiveConfirmation /></ProtectedRoute>} />
      <Route path="/candidate/deep-dive/summary" element={<ProtectedRoute><CandidateDeepDiveSummary /></ProtectedRoute>} />

      {/* Public — no auth required */}
      <Route path="/prescreen/:token" element={<CandidatePreScreen />} />
      <Route path="*" element={<NotFound />} />
    </Route>
    </>  
  )
)
